export const SCROLL_TIME = 468;

export function scrollIntoView(element: HTMLElement): Promise<void> {
    // LET THE SMOOTHNESS BEGIN!
    const scrollableParent = findScrollableParent(element);

    if (!scrollableParent) {
        return Promise.resolve();
    }

    const parentRects = scrollableParent.getBoundingClientRect();
    const clientRects = element.getBoundingClientRect();

    // reveal element inside parent
    return smoothScroll.call(
        element,
        scrollableParent,
        scrollableParent.scrollLeft + clientRects.left - parentRects.left,
        scrollableParent.scrollTop + clientRects.top - parentRects.top
    );
}

const now = window.performance && window.performance.now ? window.performance.now.bind(window.performance) : Date.now;

/**
 * scrolls window or element with a smooth behavior
 * @method smoothScroll
 * @param {Object|Node} el
 * @param {Number} x
 * @param {Number} y
 * @returns {undefined}
 */
function smoothScroll(el: HTMLElement, x: number, y: number): Promise<void> {
    return new Promise(resolve => {
        var scrollable;
        var startX;
        var startY;
        var method;
        var startTime = now();

        // define scroll context
        if (el === document.body) {
            scrollable = window;
            startX = window.scrollX || window.pageXOffset;
            startY = window.scrollY || window.pageYOffset;
            method = window.scroll;
        } else {
            scrollable = el;
            startX = el.scrollLeft;
            startY = el.scrollTop;
            method = scrollElement;
        }

        // scroll looping over a frame
        step({
            scrollable: scrollable,
            method: method,
            startTime: startTime,
            startX: startX,
            startY: startY,
            x: x,
            y: y,
            callback: resolve
        });
    });
}

/**
 * self invoked function that, given a context, steps through scrolling
 * @method step
 * @param {Object} context
 * @returns {undefined}
 */
function step(context: any) {
    var time = now();
    var value;
    var currentX;
    var currentY;
    var elapsed = (time - context.startTime) / SCROLL_TIME;

    // avoid elapsed times higher than one
    elapsed = elapsed > 1 ? 1 : elapsed;

    // apply easing to elapsed time
    value = ease(elapsed);

    currentX = context.startX + (context.x - context.startX) * value;
    currentY = context.startY + (context.y - context.startY) * value;

    context.method.call(context.scrollable, currentX, currentY);

    // scroll more if we have not reached our destination
    if (currentX !== context.x || currentY !== context.y) {
        window.requestAnimationFrame(step.bind(window, context));
    } else {
        context.callback();
    }
}

/**
 * returns result of applying ease math function to a number
 * @method ease
 * @param {Number} k
 * @returns {Number}
 */
function ease(k: number) {
    return 0.5 * (1 - Math.cos(Math.PI * k));
}

/**
 * changes scroll position inside an element
 * @method scrollElement
 * @param {Number} x
 * @param {Number} y
 * @returns {undefined}
 */
function scrollElement(this: HTMLElement, x: number, y: number) {
    this.scrollLeft = x;
    this.scrollTop = y;
}

/**
 * indicates if a the current browser is made by Microsoft
 * @method isMicrosoftBrowser
 * @param {String} userAgent
 * @returns {Boolean}
 */
function isMicrosoftBrowser(userAgent: string) {
    var userAgentPatterns = ["MSIE ", "Trident/", "Edge/"];

    return new RegExp(userAgentPatterns.join("|")).test(userAgent);
}

/*
    * IE has rounding bug rounding down clientHeight and clientWidth and
    * rounding up scrollHeight and scrollWidth causing false positives
    * on hasScrollableSpace
    */
const ROUNDING_TOLERANCE = isMicrosoftBrowser(window.navigator.userAgent) ? 1 : 0;

/**
 * finds scrollable parent of an element
 * @method findScrollableParent
 * @param {Node} el
 * @returns {Node} el
 */
function findScrollableParent(el: HTMLElement): HTMLElement | undefined {
    var isBody;

    do {
        el = el.parentNode as HTMLElement;

        isBody = el === document.body;
    } while (isBody === false && isScrollable(el as any) === false);

    isBody = null;

    return el === document.body ? undefined : el;
}

/**
 * indicates if an element can be scrolled in either axis
 * @method isScrollable
 * @param {Node} el
 * @param {String} axis
 * @returns {Boolean}
 */
function isScrollable(el: Element) {
    var isScrollableY = hasScrollableSpace(el, "Y") && canOverflow(el, "Y");
    var isScrollableX = hasScrollableSpace(el, "X") && canOverflow(el, "X");

    return isScrollableY || isScrollableX;
}

/**
 * indicates if an element has scrollable space in the provided axis
 * @method hasScrollableSpace
 * @param {Node} el
 * @param {String} axis
 * @returns {Boolean}
 */
function hasScrollableSpace(el: Element, axis: "X" | "Y"): boolean {
    if (axis === "Y") {
        return el.clientHeight + ROUNDING_TOLERANCE < el.scrollHeight;
    }

    if (axis === "X") {
        return el.clientWidth + ROUNDING_TOLERANCE < el.scrollWidth;
    }

    return false;
}

/**
 * indicates if an element has a scrollable overflow property in the axis
 * @method canOverflow
 * @param {Node} el
 * @param {String} axis
 * @returns {Boolean}
 */
function canOverflow(el: Element, axis: "X" | "Y"): boolean {
    var overflowValue = window.getComputedStyle(el, null)["overflow" + axis];

    return overflowValue === "auto" || overflowValue === "scroll";
}
